/**
 * Encaissement / CA facturé ligne — source unique pour caisse, stats lieu, historiques.
 *
 * Ventes liées à un plan de paiement (soldé ou non) : cashIn ligne = 0.
 * Les encaissements viennent uniquement de `paymentPlanPayments` (ouverture
 * fusionnée dans le ticket + versements suivants). Sinon, un plan `paid`
 * double-compte amountPaid + versements.
 */

export type CashInPrestationLike = {
  paymentPlanId?: string | null;
  paymentStatus?: string | null;
  amountPaid?: number | null;
  amountRemaining?: number | null;
  finalPrice?: number | null;
  price?: number | null;
  subtotalIncludingTax?: number | null;
};

export type CashInProductSaleLike = {
  paymentPlanId?: string | null;
  paymentStatus?: string | null;
  amountPaid?: number | null;
  amountRemaining?: number | null;
  finalPrice?: number | null;
  totalPrice?: number | null;
  subtotalIncludingTax?: number | null;
};

export type BilledLineLike = {
  subtotalIncludingTax?: number | null;
  finalPrice?: number | null;
  price?: number | null;
  totalPrice?: number | null;
};

function toFiniteAmount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Vente rattachée à un plan : cash suivi via paymentPlanPayments uniquement. */
export function isPlanSaleCashExcludedPrestation(p: CashInPrestationLike): boolean {
  return !!p.paymentPlanId;
}

export function isPlanSaleCashExcludedProduct(s: CashInProductSaleLike): boolean {
  return !!s.paymentPlanId;
}

export function hasOpenPaymentPlanStatus(
  paymentStatus: string | null | undefined
): boolean {
  return !!(paymentStatus && paymentStatus !== 'paid');
}

/** Montant TTC facturé (hors notion d’encaissement). */
export function billedTtcAmount(line: BilledLineLike): number {
  return toFiniteAmount(
    line.subtotalIncludingTax ?? line.finalPrice ?? line.price ?? line.totalPrice ?? 0
  );
}

export function billedTtcForPrestation(p: CashInPrestationLike): number {
  return billedTtcAmount(p);
}

export function billedTtcForProductSale(s: CashInProductSaleLike): number {
  return billedTtcAmount(s);
}

/** Alias historique (stats par lieu). */
export function getLineRevenueTtc(line: BilledLineLike): number {
  return billedTtcAmount(line);
}

export function cashInAmountForPrestation(p: CashInPrestationLike): number {
  if (isPlanSaleCashExcludedPrestation(p)) return 0;
  return toFiniteAmount(p.amountPaid ?? p.finalPrice ?? p.price ?? 0);
}

export function cashInAmountForProductSale(s: CashInProductSaleLike): number {
  if (isPlanSaleCashExcludedProduct(s)) return 0;
  return toFiniteAmount(s.amountPaid ?? s.finalPrice ?? s.totalPrice ?? 0);
}

/** Reste à encaisser sur une ligne (plan ou amountRemaining explicite). */
export function outstandingAmountForPrestation(p: CashInPrestationLike): number {
  if (p.amountRemaining != null && Number.isFinite(Number(p.amountRemaining))) {
    return Math.max(0, Number(p.amountRemaining));
  }
  const billed = billedTtcForPrestation(p);
  const paid = toFiniteAmount(p.amountPaid ?? 0);
  return Math.max(0, billed - paid);
}

export function outstandingAmountForProductSale(s: CashInProductSaleLike): number {
  if (s.amountRemaining != null && Number.isFinite(Number(s.amountRemaining))) {
    return Math.max(0, Number(s.amountRemaining));
  }
  const billed = billedTtcForProductSale(s);
  const paid = toFiniteAmount(s.amountPaid ?? 0);
  return Math.max(0, billed - paid);
}

export function sumCashInPrestations(lines: CashInPrestationLike[]): number {
  return lines.reduce((sum, p) => sum + cashInAmountForPrestation(p), 0);
}

export function sumCashInProductSales(lines: CashInProductSaleLike[]): number {
  return lines.reduce((sum, s) => sum + cashInAmountForProductSale(s), 0);
}

export function sumBilledTtcPrestations(lines: CashInPrestationLike[]): number {
  return lines.reduce((sum, p) => sum + billedTtcForPrestation(p), 0);
}

export function sumBilledTtcProductSales(lines: CashInProductSaleLike[]): number {
  return lines.reduce((sum, s) => sum + billedTtcForProductSale(s), 0);
}

/** Panier / ticket moyen : total / count (0 si count = 0). */
export function averageAmount(total: number, count: number): number {
  if (!count || count <= 0) return 0;
  return total / count;
}
